/**
 * CSV/XLSX have no server-generated visual "thumbnail" — spatie/medialibrary
 * (API side) can rasterize an image or a PDF's first page into a real
 * thumb_url via Imagick/Ghostscript, but there's no equivalent for a
 * spreadsheet. isCsvFile/isXlsxFile let display components detect the type
 * up front; nonImageDocIcon is the fallback (bundled csv-file.svg /
 * xlsx-file.svg) when an actual content preview isn't available or fails —
 * see SpreadsheetThumbPreview.vue and ViewFilePage.vue for the real content
 * preview built from parseCsvPreviewRows/parseXlsxPreviewRows below.
 *
 * Extension is checked first and is trustworthy here — the upload flow
 * (File::saveFiles() on the API) now derives the stored filename's extension
 * from the real, content-sniffed file type, not the client's claim, so
 * file_name reliably ends in .csv/.xlsx for genuine CSV/XLSX uploads. mime_type
 * is a fallback for anything uploaded before that fix landed.
 */

const CSV_MIME_TYPES = ['text/csv', 'application/vnd.ms-excel', 'text/plain']
const XLSX_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/zip',
]

function extensionOf(file) {
  const name = (file && (file.file_name || file.name)) || ''
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : ''
}

export function isCsvFile(file) {
  if (!file) return false
  const ext = extensionOf(file)
  if (ext === 'csv') return true
  if (ext === 'xlsx' || ext === 'pdf') return false
  return CSV_MIME_TYPES.includes(file.mime_type)
}

export function isXlsxFile(file) {
  if (!file) return false
  const ext = extensionOf(file)
  if (ext === 'xlsx') return true
  if (ext === 'csv' || ext === 'pdf') return false
  return XLSX_MIME_TYPES.includes(file.mime_type)
}

/** Bundled icon for csv/xlsx, or null if the file isn't one of those. */
export function nonImageDocIcon(file) {
  if (isCsvFile(file)) return require('@/assets/icons/csv-file.svg')
  if (isXlsxFile(file)) return require('@/assets/icons/xlsx-file.svg')
  return null
}

/**
 * Best-effort CSV parse for a content preview — naive comma split, doesn't
 * handle quoted commas/escaped quotes. That's fine here: this only ever
 * feeds a preview table, never a real import (CsvTradieImport does proper
 * parsing server-side for that).
 */
export function parseCsvPreviewRows(
  text,
  { maxRows = 15, maxCols = 8, maxCellLength = 40 } = {},
) {
  return text
    .split(/\r\n|\r|\n/)
    .filter((line) => line.length > 0)
    .slice(0, maxRows)
    .map((line) =>
      line
        .split(',')
        .slice(0, maxCols)
        .map((cell) => {
          const trimmed = cell.trim().replace(/^"|"$/g, '')
          return trimmed.length > maxCellLength
            ? trimmed.slice(0, maxCellLength) + '…'
            : trimmed
        }),
    )
}

/**
 * XLSX preview via SheetJS — unlike CSV, there's no way to hand-parse the
 * real OOXML/zip binary format, so this genuinely needs the library. Reads
 * only the first sheet (a "preview" showing every sheet in a workbook isn't
 * meaningfully different from just opening the file). Must be fetched as
 * the *whole* file (see MediaController@content's docblock) — a zip
 * truncated mid-file is corrupt, not just short, so callers must not pass a
 * small max_bytes for this the way CSV does.
 */
export function parseXlsxPreviewRows(
  arrayBuffer,
  { maxRows = 15, maxCols = 8, maxCellLength = 40 } = {},
) {
  // eslint-disable-next-line global-require
  const XLSX = require('xlsx')
  const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' })
  const firstSheetName = workbook.SheetNames[0]
  if (!firstSheetName) return []

  const sheet = workbook.Sheets[firstSheetName]
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: false,
    defval: '',
  })

  return rows.slice(0, maxRows).map((row) =>
    row.slice(0, maxCols).map((cell) => {
      const str = cell === null || cell === undefined ? '' : String(cell)
      return str.length > maxCellLength
        ? str.slice(0, maxCellLength) + '…'
        : str
    }),
  )
}

/** pdf | doc | xls | img | other, from a file's first attachment. */
export function fileKind(file) {
  const attachment = (file && file.files && file.files[0]) || file
  const ext = extensionOf(attachment)
  const mime = ((attachment && attachment.mime_type) || '').toLowerCase()

  if (ext === 'pdf' || mime.indexOf('pdf') !== -1) return 'pdf'
  if (['doc', 'docx'].includes(ext) || mime.indexOf('word') !== -1) return 'doc'
  if (isCsvFile(attachment) || isXlsxFile(attachment)) return 'xls'
  if (['jpg', 'jpeg', 'png'].includes(ext) || mime.indexOf('image/') === 0) {
    return 'img'
  }
  return 'other'
}
